// Page.tsx
import React from 'react';
import { Helmet } from 'react-helmet-async';

export interface IPage {
  /** Required page title */
  title: string;
  /** Meta description (<meta name="description">) */
  description?: string;
  /** Page author (<meta name="author">) */
  author?: string;
  /** Comma-separated keywords (<meta name="keywords">) */
  keywords?: string[];
  /** Canonical URL (<link rel="canonical"> & og:url) */
  url?: string;
  /** Preview image URL (og:image & twitter:image) */
  image?: string;
  /** Open Graph type (e.g. 'website' or 'article') */
  type?: 'website' | 'article';
  /** Open Graph site name (<meta property="og:site_name">) */
  siteName?: string;
  /** Your Twitter handle (e.g. '@YourSite') */
  twitterUsername?: string;
  /** If true, adds <meta name="robots" content="noindex, nofollow"> */
  noindex?: boolean;
  /** Document language (<html lang="…"> & og:locale) */
  lang?: string;
  children: React.ReactNode;
}

const Page: React.FC<IPage> = ({
  title,
  description = 'Join our thriving community of healthcare professionals and unlock a world of possibilities. Enhance your skills, expand your network, and shape your career on your terms.',
  author = 'Locumspace Team',
  keywords,
  url,
  image = 'https://locumspace.co/locumspace-logo.png',
  type = 'website',
  siteName = 'Locumspace',
  twitterUsername = '@locumspace',
  noindex = false,
  lang = 'en',
  children,
}) => (
  <>
    <Helmet htmlAttributes={lang ? { lang } : undefined}>
      {/* Required */}
      <title>{title}</title>

      {/* Core SEO */}
      {description && <meta name='description' content={description} />}
      {author && <meta name='author' content={author} />}
      {keywords && keywords.length > 0 && (
        <meta name='keywords' content={keywords.join(', ')} />
      )}
      <meta
        name='robots'
        content={noindex ? 'noindex, nofollow' : 'index, follow'}
      />
      {url && <link rel='canonical' href={url} />}

      {/* Additional SEO Meta Tags */}
      <meta name='application-name' content='Locumspace' />
      <meta name='theme-color' content='#000000' />
      <meta name='format-detection' content='telephone=no' />
      {lang && <meta name='language' content={lang} />}

      {/* Open Graph */}
      {siteName && <meta property='og:site_name' content={siteName} />}
      <meta property='og:type' content={type} />
      <meta property='og:title' content={title} />
      {description && <meta property='og:description' content={description} />}
      {url && <meta property='og:url' content={url} />}
      {lang && <meta property='og:locale' content={lang} />}
      {image && <meta property='og:image' content={image} />}

      {/* Twitter Card */}
      <meta
        name='twitter:card'
        content={image ? 'summary_large_image' : 'summary'}
      />
      {twitterUsername && (
        <>
          <meta name='twitter:site' content={twitterUsername} />
          <meta name='twitter:creator' content={twitterUsername} />
        </>
      )}
      <meta name='twitter:title' content={title} />
      {description && <meta name='twitter:description' content={description} />}
      {image && <meta name='twitter:image' content={image} />}
    </Helmet>

    {children}
  </>
);

export default Page;
